import { useEffect, useState } from "react";
import type { ModelCacheUsageSnapshot } from "@rnavoyager/contracts";
import { getModelCacheUsage } from "./api";

const percentage = (value: number | null) =>
  value === null ? "未报告" : `${(value * 100).toFixed(2)}%`;
const count = (value: number) => value.toLocaleString("zh-CN");
const phases = { task: "科研", arguments: "参数绑定", title: "标题", report: "报告" };
const roles = { planner: "策划／内容审核", worker: "处理／编排", vision: "视觉" };

export function AdminModelCacheUsage() {
  const [hours, setHours] = useState(24);
  const [refresh, setRefresh] = useState(0);
  const [data, setData] = useState<ModelCacheUsageSnapshot | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    const to = new Date();
    setData(null);
    setError("");
    void getModelCacheUsage(
      new Date(to.getTime() - hours * 3600000).toISOString(),
      to.toISOString(),
      controller.signal,
    )
      .then((value) => {
        if (!controller.signal.aborted) setData(value);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError("缓存统计加载失败，请重试。");
      });
    return () => controller.abort();
  }, [hours, refresh]);
  return (
    <section className="usage-user-card" aria-label="模型缓存统计">
      <h3>模型缓存统计</h3>
      <div className="session-usage-tabs">
        {[1, 24, 168].map((value) => (
          <button key={value} aria-pressed={hours === value} onClick={() => setHours(value)}>
            {value === 168 ? "最近 7 天" : `最近 ${value} 小时`}
          </button>
        ))}
        <button onClick={() => setRefresh((value) => value + 1)}>刷新缓存统计</button>
      </div>
      {error ? (
        <p role="alert">{error}</p>
      ) : !data ? (
        <p>正在加载缓存统计…</p>
      ) : (
        <>
          <p>
            加权命中率 {percentage(data.totals.hitRatio)} · 缓存明细覆盖率{" "}
            {percentage(data.totals.requestCoverage)} · 输入 token 覆盖率{" "}
            {percentage(data.totals.inputTokenCoverage)}
          </p>
          <p>
            厂商请求 {count(data.totals.requests)} · 已闭合 {count(data.totals.closedRequests)} ·
            进行中 {count(data.totals.pendingRequests)} · 零命中{" "}
            {count(data.totals.zeroHitRequests)} · 未报告输入用量{" "}
            {count(data.totals.unknownUsageRequests)}
          </p>
          {data.groups.length ? (
            <div className="session-usage-body">
              <table className="session-usage-table">
                <thead>
                  <tr>
                    <th>模型</th>
                    <th>链路</th>
                    <th>阶段／角色</th>
                    <th>已闭合请求</th>
                    <th>输入 token</th>
                    <th>缓存 token</th>
                    <th>命中率</th>
                    <th>覆盖率</th>
                  </tr>
                </thead>
                <tbody>
                  {data.groups.map((group) => (
                    <tr
                      key={`${group.adapter}/${group.modelConfigDigest}/${group.phase}/${group.role}`}
                    >
                      <td>{group.modelName}</td>
                      <td>{group.adapter}</td>
                      <td>
                        {phases[group.phase]}／{roles[group.role]}
                      </td>
                      <td>{count(group.closedRequests)}</td>
                      <td>{count(group.knownInputTokens)}</td>
                      <td>
                        {group.cacheKnownRequests ? count(group.cachedInputTokens) : "未报告"}
                      </td>
                      <td>{percentage(group.hitRatio)}</td>
                      <td>{percentage(group.requestCoverage)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>所选时间范围暂无缓存观测。</p>
          )}
          <p className="muted">
            仅统计实际厂商请求；Codex 子请求逐次统计。历史缓存计数未记录，不按零处理。
            {data.observedFrom
              ? `首次观测 ${new Date(data.observedFrom).toLocaleString("zh-CN")}`
              : "尚无观测记录"}
            。
          </p>
        </>
      )}
    </section>
  );
}
